import getSlug from 'speakingurl';
import { Action } from '../../types/action';
import { AppState, Store, CSSEvent, User, Breadcrumb } from '../../types/model';
import {
  byStore,
  digitalEvents,
  formatEvents,
  getStoreDigits,
  toMap,
} from './utils';
import {
  RESET_ERROR,
  SELECT_EVENT,
  SELECT_STORE,
  SET_ERROR,
  SET_EVENTS,
  SET_STORES,
  SELECT_TIMESLOT,
  SET_USER,
  SET_BREADCRUMBS,
  SET_EVENT,
} from './actionTypes';
import { config } from '../../config';

export const initialState: AppState = {
  stores: {},
  selectedStore: null,
  pageError: null,
  events: {},
  byStore: {},
  digitalEvents: [],
  selectedEvent: null,
  selectedTimeslot: null,
  user: null,
  breadcrumbs: [],
};

export default function reducer(state: AppState, action: Action) {
  switch (action.type) {
    case SET_STORES:
      const stores = action.payload.map((s: Store) => ({
        ...s,
        slug: config.storeNoInUrl
          ? getStoreDigits(s.storeNo)
          : getSlug(s.storeName),
      }));
      return {
        ...state,
        stores: toMap(stores, 'storeNo'),
      };
    case SELECT_STORE:
      return {
        ...state,
        selectedStore: action.payload,
      };
    case SET_EVENTS:
      const events = formatEvents(
        action.payload.events as Array<CSSEvent>,
        {
          language: config.language,
          defaultLanguage: config.defaultLanguage,
        },
        action.payload.filterPastEvents
      );

      return {
        ...state,
        events: toMap(events, 'id'),
        byStore: byStore(events),
        digitalEvents: digitalEvents(events),
      };
    case SET_EVENT:
      const [event] = formatEvents([action.payload] as Array<CSSEvent>, {
        language: config.language,
        defaultLanguage: config.defaultLanguage,
      });

      return {
        ...state,
        events: {
          ...state.events,
          [event.id]: event,
        },
      };
    case SELECT_EVENT:
      return {
        ...state,
        selectedEvent: action.payload,
      };
    case SELECT_TIMESLOT:
      return {
        ...state,
        selectedTimeslot: action.payload,
      };
    case SET_USER:
      return {
        ...state,
        user: action.payload,
      };
    case SET_BREADCRUMBS:
      return {
        ...state,
        breadcrumbs: action.payload,
      };
    case SET_ERROR:
      return {
        ...state,
        pageError: { code: action.payload },
      };
    case RESET_ERROR:
      return {
        ...state,
        pageError: null,
      };
    default:
      return state;
  }
}

export const setStores = (stores: Array<Store>) => ({
  type: SET_STORES,
  payload: stores,
});

export const selectStore = (storeNo: string | null) => ({
  type: SELECT_STORE,
  payload: storeNo,
});

export const setEvents = (
  events: Array<CSSEvent>,
  filterPastEvents: boolean = true
) => ({
  type: SET_EVENTS,
  payload: { events, filterPastEvents },
});

export const setEvent = (event: CSSEvent) => ({
  type: SET_EVENT,
  payload: event,
});

export const selectEvent = (eventId: string | null) => ({
  type: SELECT_EVENT,
  payload: eventId,
});

export const selectTimeslot = (timeslotId: string | undefined) => ({
  type: SELECT_TIMESLOT,
  payload: timeslotId,
});

export const setUser = (user: User | null) => ({
  type: SET_USER,
  payload: user,
});

export const setBreadcrumbs = (breadcrumbs: Array<Breadcrumb>) => ({
  type: SET_BREADCRUMBS,
  payload: breadcrumbs,
});

export const setError = (code?: number) => ({
  type: SET_ERROR,
  payload: code,
});

export const resetError = () => ({ type: RESET_ERROR });
